import React, { useEffect, useState } from "react"
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../reusable/Dialog"
import type { PermissionGateProps } from "~/components/design-system/components"
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "../reusable/Form"
import { Input } from "../reusable/Input"
import Select, { type ReactSelectOption } from "../reusable/Select"
import DatePicker from "~/components/reusable/DatePicker"
import { useForm } from "react-hook-form"
import { Button } from "../reusable/Button"
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "../reusable/Tabs"
import clsx from "clsx"
import { addDays } from "date-fns"
import { isDemo } from "~/utils/environment"

export type PushBackDialogValues = Record<string, string>

interface SchemaField {
  name: string
  label: string
  type: string
  placeholder?: string
  options?: ReactSelectOption<string>[]
  functions?: {
    [functionName: string]: {
      trigger: string
      condition?: string | ((values: Array<any>) => boolean)
      params?: string[]
      resultText?: string
    }
  }
}

/** A group of POs that share a configuration in the dialog (typically one per collection). */
export interface PushBackGroup {
  id: string
  label: string
  purchaseOrderIds: number[]
}

/** Per-group submission emitted on confirm. */
export interface PushBackSubmission {
  purchaseOrderIds: number[]
  values: PushBackDialogValues
}

type PushBackDialogProps = PermissionGateProps & {
  trigger: React.ReactNode
  disabled?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (submissions: PushBackSubmission[]) => void
  defaultValues?: Partial<PushBackDialogValues>
  schema: SchemaField[]
  availableFunctions?: Record<string, (...args: any[]) => any>
  /** PO groups to configure. With ≥2 groups, the user can choose per-group or global config. */
  groups: PushBackGroup[]
}

const GLOBAL_KEY = "__global__"

type Mode = "per-group" | "global"

const PushBackDialog: React.FC<PushBackDialogProps> = ({
  trigger,
  disabled,
  open,
  onOpenChange,
  onConfirm,
  defaultValues,
  schema,
  availableFunctions = {},
  groups,
  ...gateProps
}) => {
  const isSchemaEven = schema.length % 2 === 0
  const gridColsClass = isSchemaEven ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
  const supportsToggle = groups.length >= 2

  const buildInitDefaults = (): Record<string, PushBackDialogValues> => {
    const groupDefaults: PushBackDialogValues = {}
    schema.forEach((f) => {
      groupDefaults[f.name] = defaultValues?.[f.name] ?? ""
    })
    const result: Record<string, PushBackDialogValues> = {
      [GLOBAL_KEY]: { ...groupDefaults },
    }
    for (const group of groups) {
      result[group.id] = { ...groupDefaults }
    }
    return result
  }

  const form = useForm<Record<string, PushBackDialogValues>>({
    defaultValues: buildInitDefaults(),
    mode: "onChange",
  })

  const [mode, setMode] = useState<Mode>(supportsToggle ? "per-group" : "global")
  const [activeTabId, setActiveTabId] = useState<string>(groups[0]?.id ?? "")

  // Reset mode (and form values) to defaults whenever the dialog opens.
  useEffect(() => {
    if (!open) return
    setMode(supportsToggle ? "per-group" : "global")
    setActiveTabId(groups[0]?.id ?? "")
    form.reset(buildInitDefaults())
  }, [open])

  const evaluateCondition = (
    condition: string | ((values: Array<any>) => boolean) | undefined,
    values: Array<any>,
  ): boolean => {
    if (!condition) return true
    if (typeof condition === "function") return condition(values)
    switch (condition) {
      case "distinct_over_1":
        return Object.values(values).filter((value) => !!value).length > 1
      case "always":
        return true
      case "never":
        return false
      default:
        return true
    }
  }

  const executeDynamicFunction = (fieldDef: SchemaField, functionName: string) => {
    const functionConfig = fieldDef.functions?.[functionName]
    if (!functionConfig || !availableFunctions[functionName]) return null
    const result = availableFunctions[functionName]()
    if (!evaluateCondition(functionConfig.condition, result)) return null
    return result
  }

  // Watch the entire form so validity checks are reactive.
  const watchedValues = form.watch()

  const isGroupValid = (groupId: string): boolean => {
    const values = watchedValues[groupId] ?? {}
    return schema.every((f) => {
      const v = values[f.name]
      return typeof v === "string" && v.length > 0
    })
  }

  const allGroupsValid = groups.every((g) => isGroupValid(g.id))
  const submitEnabled = !isDemo() && (mode === "global" ? isGroupValid(GLOBAL_KEY) : allGroupsValid)

  const handleSubmit = () => {
    const values = form.getValues()
    let submissions: PushBackSubmission[]
    if (mode === "global") {
      const allPurchaseOrderIds = groups.flatMap((g) => g.purchaseOrderIds)
      submissions = [{ purchaseOrderIds: allPurchaseOrderIds, values: values[GLOBAL_KEY] ?? {} }]
    } else {
      submissions = groups.map((g) => ({
        purchaseOrderIds: g.purchaseOrderIds,
        values: values[g.id] ?? {},
      }))
    }
    onConfirm(submissions)
    onOpenChange(false)
  }

  const renderSchemaFields = (groupKey: string) => (
    <div className={clsx("grid", gridColsClass, "gap-4")}>
      {schema.map((fieldDef) => (
        <div key={fieldDef.name} className="flex flex-col gap-2">
          <FormField
            control={form.control}
            name={`${groupKey}.${fieldDef.name}` as const}
            rules={{ required: `${fieldDef.label} is required` }}
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>{fieldDef.label}</FormLabel>
                  <FormControl>
                    {fieldDef.type === "select" && fieldDef.options ? (
                      <Select
                        options={fieldDef.options}
                        value={fieldDef.options.find((o) => o.value === field.value)}
                        onChange={(opt) => field.onChange(opt?.value ?? "")}
                        placeholder={fieldDef.placeholder}
                        isClearable
                      />
                    ) : fieldDef.type === "date" ? (
                      <DatePicker
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) => {
                          field.onChange(date ? date.toISOString() : "")
                        }}
                        minDate={addDays(new Date(), 1)}
                        fullWidth
                      />
                    ) : (
                      <Input
                        {...field}
                        placeholder={fieldDef.placeholder}
                        containerClassName="w-full"
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )
            }}
          />

          {fieldDef.functions &&
            Object.entries(fieldDef.functions).map(([functionName, functionConfig]) => {
              if (functionConfig.trigger === fieldDef.name) {
                const result = executeDynamicFunction(fieldDef, functionName)
                if (result) {
                  return (
                    <div key={functionName}>
                      <p className="mt-1 text-fg-muted text-[10px]">
                        {functionConfig.resultText} {result.join(", ")}
                      </p>
                    </div>
                  )
                }
              }
              return null
            })}
        </div>
      ))}
    </div>
  )

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild disabled={disabled} {...gateProps}>
        {trigger}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Purchase Orders to ERP</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (submitEnabled) handleSubmit()
            }}
            className="flex flex-col gap-4"
          >
            {supportsToggle && (
              <TabsRoot value={mode} onValueChange={(v) => setMode(v as Mode)}>
                <TabsList size="sm">
                  <TabsTrigger value="per-group" size="sm">
                    Per-collection configuration
                  </TabsTrigger>
                  <TabsTrigger value="global" size="sm">
                    Global configuration
                  </TabsTrigger>
                </TabsList>
              </TabsRoot>
            )}

            {mode === "global" || !supportsToggle ? (
              renderSchemaFields(GLOBAL_KEY)
            ) : (
              <TabsRoot value={activeTabId} onValueChange={setActiveTabId}>
                <TabsList>
                  {groups.map((g) => {
                    const valid = isGroupValid(g.id)
                    return (
                      <TabsTrigger key={g.id} value={g.id}>
                        <span className="flex items-center gap-1.5">
                          {g.label}
                          {!valid && (
                            <span className="h-1.5 w-1.5 rounded-full bg-fg-danger" aria-hidden />
                          )}
                        </span>
                      </TabsTrigger>
                    )
                  })}
                </TabsList>
                {groups.map((g) => (
                  <TabsContent key={g.id} value={g.id}>
                    {renderSchemaFields(g.id)}
                  </TabsContent>
                ))}
              </TabsRoot>
            )}

            <DialogFooter className="flex justify-end gap-2 mt-4">
              <Button variant="transparent" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!submitEnabled}>
                Send to ERP
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </DialogRoot>
  )
}

export default PushBackDialog
